import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../components/utils';
import { AthleteWithTeam, CategorizedWorkouts, WorkoutData } from '../types';
import { WorkoutItem } from './WorkoutItem';

type WorkoutSelectionProps = {
  athlete: AthleteWithTeam;
  athleteCategorizedWorkouts: CategorizedWorkouts;
};

export const WorkoutSelection = ({
  athleteCategorizedWorkouts,
  athlete,
}: WorkoutSelectionProps) => {
  const { t } = useTranslation();
  const { today, missing, upcoming, flex } = athleteCategorizedWorkouts;

  return (
    <div className="flex gap-4 grow p-6 overflow-hidden">
      <div className="flex flex-col flex-1 gap-4 min-w-0 overflow-hidden">
        <WorkoutSection
          athlete={athlete}
          title={t('today')}
          workouts={today}
          emptyMessage={t('noWorkoutsAssignedOnThisDay')}
        />
        <WorkoutSection
          athlete={athlete}
          title={t('past')}
          workouts={missing}
        />
      </div>

      <div className="flex flex-col flex-1 gap-4 min-w-0 overflow-hidden">
        <WorkoutSection
          athlete={athlete}
          title={t('upcoming')}
          workouts={upcoming}
          emptyMessage={t('dashboard.noUpcomingWorkouts')}
        />
        <WorkoutSection
          athlete={athlete}
          title={t('anytime')}
          workouts={flex}
        />
      </div>
    </div>
  );
};

type WorkoutSectionProps = {
  athlete: AthleteWithTeam;
  title: string;
  workouts: WorkoutData[];
  emptyMessage?: string;
};

const WorkoutSection = ({
  athlete,
  title,
  workouts,
  emptyMessage,
}: WorkoutSectionProps) => {
  const { t } = useTranslation();
  if (workouts.length === 0 && !emptyMessage) {
    return null;
  }

  return (
    <div className="flex flex-col overflow-hidden grow min-h-[130px] max-h-full flex-1">
      <Typography
        variant="h3"
        className={cn(title === t('past') && 'mt-2', 'mb-2 flex-shrink-0')}
      >
        {title}
      </Typography>
      <div className="flex flex-col grow overflow-y-auto border border-gray-200 rounded-lg p-3">
        {workouts.length > 0 ? (
          <div className="flex flex-col flex-1 gap-2">
            {workouts.map((workout) => (
              <WorkoutItem
                key={workout.workoutId}
                workout={workout}
                athlete={athlete}
              />
            ))}
          </div>
        ) : (
          <Typography
            variant="body2"
            className="text-gray-500 text-center flex flex-col flex-1 justify-center items-center flex-shrink-0"
          >
            {emptyMessage}
          </Typography>
        )}
      </div>
    </div>
  );
};
